/**
 * Маршрут /tasks: последняя открытая доска, иначе первая доступная (§3.4).
 *
 * /tasks?department=КОД — вкладка подразделения (§8.5): его последняя или
 * первая доска, а если досок в подразделении нет — те же вкладки и
 * предложение создать доску. Прежде здесь был отдельный список карточек,
 * которого нет в макете: переключаться между подразделениями и досками
 * можно было только через него.
 *
 * Последняя доска хранится локально (§3.5). Если доступ к ней утрачен,
 * открывается первая доступная, а не недоступная.
 */

import { DepartmentCode, Permission } from "@taskflow/contracts";
import { LayoutGrid } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import { useAppSelector } from "@/app/hooks";
import { useDepartmentsQuery } from "@taskflow/client-core/directory";
import { errorMessage, usePermission } from "@taskflow/client-core";
import { Button, EmptyState, ErrorState, SkeletonRows } from "@/shared/ui";
import { Plus } from "@/shared/ui/motionIcons";

import { useBoardsQuery, useDepartmentTabs } from "@taskflow/client-core/boards";
import { BoardSwitcher } from "./BoardSwitcher";
import { CreateBoardDialog } from "./CreateBoardDialog";
import { pickBoardFor, readLastBoardId } from "./lastBoard";

export function BoardsPage() {
  const boards = useBoardsQuery();
  const departments = useDepartmentsQuery();
  const tabs = useDepartmentTabs();
  const user = useAppSelector((state) => state.auth.user);
  const canCreate = usePermission(Permission.BOARD_CREATE);
  const [params] = useSearchParams();
  const [creating, setCreating] = useState(false);

  if (boards.isLoading || departments.isLoading) return <SkeletonRows rows={4} className="p-6" />;

  if (boards.error !== undefined) {
    return (
      <ErrorState
        title="Не удалось загрузить доски"
        message={errorMessage(boards.error, "Попробуйте ещё раз")}
        onRetry={() => void boards.refetch()}
      />
    );
  }

  const available = boards.data?.data ?? [];
  const requested = params.get("department");
  const department = tabs.order.find((code) => code === requested);
  const primaryCode = departments.data?.data.find(
    (item) => item.id === user?.primaryDepartmentId,
  )?.code;

  if (department === undefined) {
    const lastBoardId = readLastBoardId();
    const target =
      available.find((board) => board.id === lastBoardId)?.id ??
      (primaryCode === undefined ? null : pickBoardFor(primaryCode, available)) ??
      available[0]?.id ??
      null;
    if (target !== null) return <Navigate to={`/boards/${target}`} replace />;
  } else {
    const target = pickBoardFor(department, available);
    if (target !== null) return <Navigate to={`/boards/${target}`} replace />;
  }

  // Досок нет — в выбранном подразделении или вовсе.
  const shownCode = department ?? primaryCode ?? DepartmentCode.OA;
  const shownLabel = tabs.labels[shownCode] ?? shownCode;
  const shownDepartmentId = departments.data?.data.find((item) => item.code === shownCode)?.id;

  return (
    <div className="flex flex-col">
      <header className="flex shrink-0 flex-col gap-2 border-b border-line bg-surface px-4 py-2.5">
        <BoardSwitcher departmentCode={shownCode} boardId={null} />
      </header>

      <EmptyState
        icon={LayoutGrid}
        title={
          available.length === 0 ? "Доступных досок нет" : `В подразделении ${shownLabel} пока нет досок`
        }
        description={
          canCreate
            ? "Создайте доску или попросите добавить вас в существующую."
            : "Создайте личную доску или попросите руководителя добавить вас в доску подразделения."
        }
        action={
          <div className="flex flex-wrap justify-center gap-2">
            {/* Личную доску заводит любой сотрудник. */}
            <Button icon={Plus} onClick={() => setCreating(true)}>
              {canCreate ? "Создать доску" : "Создать личную доску"}
            </Button>
            {/* Все доски могли уйти в архив — вернуть их можно только оттуда. */}
            <Link to="/boards/archived">
              <Button variant="secondary">Архив досок</Button>
            </Link>
          </div>
        }
      />

      {creating && (
        <CreateBoardDialog
          open
          onClose={() => setCreating(false)}
          {...(shownDepartmentId === undefined ? {} : { initialDepartmentId: shownDepartmentId })}
        />
      )}
    </div>
  );
}
